import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import React from 'react';
import { MARK_TYPE } from '../../constants/shared/ProbeDetailsConstants';
import { getClassNameByCondition } from './commonUtils';

const { INSERT, SUBSTITUTE, REPEAT, SELF_CORRECT, OMIT, CORRECT } = MARK_TYPE;
const REPEAT_RIGHT_BORDER = 'repeat-right-border';
const REPEAT_LEFT_BORDER = 'repeat-left-border';
const REPEAT_CENTER = 'repeat-center';

const getMarkByType = (marks = [], markType = '') =>
    marks.find(mark => mark.eventType === markType);

export const getInsertMark = marks => getMarkByType(marks, INSERT);

export const getSubstituteMark = marks => getMarkByType(marks, SUBSTITUTE);

export const getOmitMark = marks => getMarkByType(marks, OMIT);

export const getCorrectMark = marks => getMarkByType(marks, CORRECT);

export const isChoosenCorrectionWord = (endPosition, wordPosition) => {
    return !isNil(endPosition) ? wordPosition <= endPosition : true;
};

export const isEndPosition = (wordPosition, sentenceLength) => wordPosition + 1 === sentenceLength;

const getStartRepeatClassName = (repeatStartPosition, marks) => {
    const insertMark = getMarkByType(marks, INSERT);

    if (insertMark) {
        const { position } = insertMark.eventDetails;
        return position === repeatStartPosition ? REPEAT_CENTER : REPEAT_LEFT_BORDER;
    }
    return REPEAT_LEFT_BORDER;
};

const getInsertRepeatClassName = (index, startPosition, isEnd) => {
    const startRepeatClassName = index === startPosition ? REPEAT_LEFT_BORDER : REPEAT_CENTER;
    return isEnd ? REPEAT_RIGHT_BORDER : startRepeatClassName;
};

export const getRepeatClassName = (marks, index, isEnd) => {
    const repeatMark = getMarkByType(marks, REPEAT);
    let repeatClassName = '';
    let insertRepeatClassName = '';

    if (repeatMark) {
        const { startPosition, endPosition } = repeatMark.eventDetails;

        const startRepeatClassName = getStartRepeatClassName(startPosition, marks);
        const endRepeatClassName = index === endPosition ? REPEAT_RIGHT_BORDER : REPEAT_CENTER;

        repeatClassName =
            index === startPosition
                ? `${startRepeatClassName} ${endRepeatClassName}`
                : endRepeatClassName;
        insertRepeatClassName = getInsertRepeatClassName(index, startPosition, isEnd);
    }

    return {
        repeatClassName,
        insertRepeatClassName,
    };
};

export const getOmitClassName = marks => {
    const selfCorrectMark = get(getMarkByType(marks, SELF_CORRECT), 'eventType', '');
    return isEmpty(selfCorrectMark) ? get(getMarkByType(marks, OMIT), 'eventType', '') : '';
};

export const getSubstituteSelfCorrectClassName = marks => {
    const substituteMark = get(getMarkByType(marks, SUBSTITUTE), 'eventType', '');
    const selfCorrectMark = get(getMarkByType(marks, SELF_CORRECT), 'eventType', '');
    return `${substituteMark} ${selfCorrectMark}`;
};

export const getInsertSelfCorrectClassName = (marks, isEnd) => {
    const insertMark = get(getMarkByType(marks, INSERT), 'eventType', '');
    const selfCorrectMark = get(getMarkByType(marks, SELF_CORRECT), 'eventType', '');
    return `${insertMark} ${isEnd ? selfCorrectMark : ''}`;
};

export const getWordContent = (word, wordClasses, wordWrapperClasses) => (
    <span className={`word ${wordWrapperClasses}`}>
        <span className={`text ${wordClasses}`}>{word}</span>
    </span>
);

export const getInsertMarkContent = ({
    insertMark,
    wordWrapperProperties,
    insertWordWrapperClasses,
    insertWordClasses,
}) => (
    <span {...wordWrapperProperties} className={`word ${insertWordWrapperClasses}`}>
        <span className={insertWordClasses}>{insertMark.eventDetails.annotationContent}</span>
    </span>
);

export const getSubstituteMarkContent = ({
    word,
    wordClasses,
    substituteMark,
    wordWrapperClasses,
    substituteWordClasses,
}) => (
    <>
        {substituteMark && (
            <span className="sub-word">
                <span className={substituteWordClasses}>
                    {substituteMark.eventDetails.annotationContent}
                </span>
            </span>
        )}
        <span className={`word ${wordWrapperClasses}`}>
            <span className={`text ${wordClasses}`}>{word}</span>
        </span>
    </>
);

export const getOlMarksClassNames = (marks, wordPosition, isEnd, isItalic) => {
    // Repeat class names
    const { repeatClassName, insertRepeatClassName } = getRepeatClassName(
        marks,
        wordPosition,
        isEnd,
    );

    // Word classname
    const italicClassName = getClassNameByCondition(isItalic, 'font-italic');

    // Insert self correct classname
    const insertWordClasses = `ins-text ${getInsertSelfCorrectClassName(marks, isEnd)}`;

    // Word wrapper classes
    const insertWordWrapperClasses = `${insertRepeatClassName} ${italicClassName}`;

    // Substitute word wrapper classes
    const wordWrapperClasses = `${repeatClassName} ${italicClassName}`;

    // Word classes
    const wordClasses = `${getSubstituteSelfCorrectClassName(marks)} ${getOmitClassName(marks)}`;

    // Substitute word classes
    const substituteWordClasses = `sub-text ${getSubstituteSelfCorrectClassName(marks)}`;

    return {
        insertWordClasses,
        wordWrapperClasses,
        insertWordWrapperClasses,
        wordClasses,
        substituteWordClasses,
    };
};
